// F/H. Security incidents, government demands, AI governance and the
// automated-decision framework.
//
// Every screen here repeats the same boundary: the system tracks and
// assists, a named person decides and acts. Nothing notifies a regulator,
// emails affected users, or discloses data.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  CAP, can, fmtDate, fmtDateTime, humanise, legalError, plGet, plPost,
} from "../../lib/platformLegal";
import {
  Button, ClockPill, Empty, Loading, Modal, Note, Section, SeverityPill, Stat, Table, TonePill,
} from "./ui";

const SUBTABS = [
  { key: "incidents", label: "Security incidents", cap: CAP.privacyBreach },
  { key: "requests", label: "Government demands", cap: CAP.legalRequests },
  { key: "ai", label: "AI governance", cap: CAP.ai },
  { key: "automated", label: "Automated decisions", cap: CAP.view },
];

export default function SecuritySection({ capabilities }) {
  const available = SUBTABS.filter((t) => can(capabilities, t.cap));
  const [tab, setTab] = useState(available[0]?.key || "automated");

  if (available.length === 0) {
    return <Empty>You do not have access to any security register.</Empty>;
  }
  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <div className="flex gap-1.5 min-w-max" role="tablist" data-testid="pl-security-tabs">
          {available.map((item) => (
            <button key={item.key} role="tab" aria-selected={tab === item.key}
                    onClick={() => setTab(item.key)}
                    data-testid={`pl-security-tab-${item.key}`}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-medium whitespace-nowrap ${
                      tab === item.key ? "border-slate-900 bg-slate-900 text-white"
                                       : "border-slate-200 bg-white text-slate-700"}`}>
              {item.label}
            </button>
          ))}
        </div>
      </div>
      {tab === "incidents" && <Incidents />}
      {tab === "requests" && <LegalRequests />}
      {tab === "ai" && <AiGovernance />}
      {tab === "automated" && <AutomatedDecisions capabilities={capabilities} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
function Incidents() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try { setData(await plGet("/security-incidents")); }
    catch (err) { toast.error(legalError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading label="Reading the incident register..." />;

  const board = data.dashboard;
  return (
    <>
      <Section testId="pl-incident-board" title="Security incidents and personal data breaches"
               description={board.decision_note}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Open" value={board.open} testId="pl-incidents-open" />
          <Stat label="Critical" value={board.critical}
                tone={board.critical ? "text-rose-700" : "text-slate-900"} />
          <Stat label="Awaiting assessment" value={board.awaiting_assessment}
                tone={board.awaiting_assessment ? "text-amber-700" : "text-slate-900"} />
          <Stat label="Window (hours)" value={board.window_hours} />
        </div>
      </Section>

      <Section testId="pl-incidents">
        {data.incidents.length === 0 ? (
          <Empty testId="pl-incidents-empty">No open incidents.</Empty>
        ) : (
          <Table head={["Reference", "Incident", "Severity", "Window", "Decision"]}
                 testId="pl-incident-table" minWidth={680}>
            {data.incidents.map((row) => (
              <tr key={row.id} data-testid={`pl-incident-${row.reference}`}>
                <td className="py-2 pr-3">
                  <button onClick={() => setOpen(row)}
                          className="font-mono text-xs text-slate-900 hover:underline">
                    {row.reference}
                  </button>
                  <div className="text-[11px] text-slate-500">{fmtDateTime(row.detected_at)}</div>
                </td>
                <td className="py-2 pr-3 text-xs">
                  <div className="font-medium text-slate-900">{row.title}</div>
                  {humanise(row.incident_type)}
                </td>
                <td className="py-2 pr-3"><SeverityPill severity={row.severity} /></td>
                <td className="py-2 pr-3"><ClockPill clock={row.notification_window} /></td>
                <td className="py-2 pr-3 text-xs">{humanise(row.reportable_decision)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      {open && (
        <IncidentModal incident={open} options={data} onClose={() => setOpen(null)}
                       onChanged={load} />
      )}
    </>
  );
}

function IncidentModal({ incident, options, onClose, onChanged }) {
  const [detail, setDetail] = useState(incident);
  const [busy, setBusy] = useState(false);

  const act = async (path, body, message) => {
    setBusy(true);
    try {
      setDetail(await plPost(`/security-incidents/${incident.id}/${path}`, body));
      toast.success(message);
      onChanged();
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`${detail.reference}: ${detail.title}`} onClose={onClose}
           testId="pl-incident-modal" description={humanise(detail.incident_type)}>
      <div className="flex flex-wrap items-center gap-2">
        <SeverityPill severity={detail.severity} />
        <ClockPill clock={detail.notification_window} />
        <span className="text-xs text-slate-600">{humanise(detail.status)}</span>
      </div>
      <p className="text-sm text-slate-800 whitespace-pre-wrap">{detail.description}</p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <dt className="text-slate-500">Affected data</dt>
        <dd className="text-right">
          {(detail.affected_data_categories || []).map(humanise).join(", ") || "—"}
        </dd>
        <dt className="text-slate-500">Estimated records</dt>
        <dd className="text-right tabular-nums">{detail.estimated_records ?? "—"}</dd>
        <dt className="text-slate-500">Regulator notification</dt>
        <dd className="text-right">{humanise(detail.npc_notification_state)}</dd>
        <dt className="text-slate-500">Affected-user notification</dt>
        <dd className="text-right">{humanise(detail.user_notification_state)}</dd>
      </dl>
      <Note tone="amber" testId="pl-incident-decision-note">{detail.decision_note}</Note>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button variant="secondary" busy={busy} onClick={() => {
          const decision = window.prompt(
            `Decision (${(options.decisions || []).join(", ")}):`);
          if (!decision) return;
          const reason = window.prompt("Record the assessment this decision rests on:");
          if (reason) act("decision", { decision, reason }, "Decision recorded.");
        }} data-testid="pl-incident-decide">Record breach decision</Button>

        <Button variant="secondary" busy={busy} onClick={() => {
          const channel = window.prompt("Channel (npc / affected_users):");
          if (!channel) return;
          const reference = window.prompt("Submission reference (optional):") || "";
          const note = window.prompt("What was sent, and to whom?");
          if (note) act("notification", { channel, state: "submitted", reference, note },
                        "Notification recorded.");
        }}>Record a notification sent</Button>

        <Button variant="secondary" busy={busy} onClick={() => {
          const postmortem = window.prompt("Postmortem: cause, impact, fixes.");
          if (postmortem) act("close", { postmortem }, "Incident closed.");
        }}>Close incident</Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
function LegalRequests() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData(await plGet("/legal-requests")); } catch (err) { toast.error(legalError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading label="Reading the legal request register..." />;

  return (
    <Section testId="pl-legal-requests" title="Government and law-enforcement requests"
             description="Nothing is disclosed from this screen. It records the demand, the
                          review, the decision and what was actually handed over.">
      {data.requests.length === 0 ? (
        <Empty testId="pl-legal-requests-empty">No requests recorded.</Empty>
      ) : (
        <Table head={["Reference", "Type", "Authority", "Received", "Decision"]}
               testId="pl-legal-request-table" minWidth={640}>
          {data.requests.map((row) => (
            <tr key={row.id} data-testid={`pl-legal-request-${row.reference}`}>
              <td className="py-2 pr-3 font-mono text-xs">{row.reference}</td>
              <td className="py-2 pr-3 text-xs">{humanise(row.request_type)}</td>
              <td className="py-2 pr-3 text-xs">{row.authority}</td>
              <td className="py-2 pr-3 text-xs">{fmtDate(row.received_at)}</td>
              <td className="py-2 pr-3 text-xs">
                {humanise(row.disclosure_decision)}
                {row.decided_by_name && (
                  <div className="text-slate-500">{row.decided_by_name}</div>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
      <Note tone="slate">
        Disclosing personal data to an authority requires a named legal basis, recorded on the
        request alongside who decided and why.
      </Note>
    </Section>
  );
}

// ---------------------------------------------------------------------------
function AiGovernance() {
  const [data, setData] = useState(null);
  useEffect(() => {
    plGet("/ai-governance").then(setData).catch((err) => toast.error(legalError(err)));
  }, []);
  if (!data) return <Loading label="Reading the AI register..." />;

  return (
    <>
      <Section testId="pl-ai-integrations" title="AI integrations"
               description="Every place ConfirmPH can send data to a model provider. A new
                            integration that is not registered here is a visible omission.">
        <Table head={["Integration", "Purpose", "Provider", "Decision authority"]}
               testId="pl-ai-table" minWidth={640}>
          {data.integrations.map((row) => (
            <tr key={row.key} data-testid={`pl-ai-${row.key}`}>
              <td className="py-2 pr-3">
                <div className="font-medium text-slate-900">{row.name}</div>
                <div className="text-[11px] text-slate-500 font-mono">{row.module}</div>
              </td>
              <td className="py-2 pr-3 text-xs">{row.purpose}</td>
              <td className="py-2 pr-3 text-xs">
                {row.provider}
                <div className="text-slate-500">
                  {row.configured ? "Configured" : "Not configured"}
                </div>
              </td>
              <td className="py-2 pr-3 text-xs">{row.decision_authority}</td>
            </tr>
          ))}
        </Table>
        <Note tone="slate" testId="pl-ai-note">{data.usage.note}</Note>
      </Section>

      <Section testId="pl-ai-guard" title="Data egress guard">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Calls (30d)" value={data.usage.calls} />
          <Stat label="Refused" value={data.usage.refused}
                tone={data.usage.refused ? "text-rose-700" : "text-slate-900"} />
          <Stat label="With redaction" value={data.usage.calls_with_redaction} />
          <Stat label="Prompt text stored" value={data.usage.prompt_content_stored ? "Yes" : "No"} />
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div>
            <div className="font-semibold uppercase tracking-[0.12em] text-slate-500">
              Redacted before egress
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {data.redacted_kinds.map((kind) => (
                <TonePill key={kind} tone="border-amber-200 bg-amber-50 text-amber-900">
                  {kind}
                </TonePill>
              ))}
            </div>
          </div>
          <div>
            <div className="font-semibold uppercase tracking-[0.12em] text-slate-500">
              Refused outright
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {data.refused_kinds.map((kind) => (
                <TonePill key={kind} tone="border-rose-200 bg-rose-50 text-rose-700">
                  {kind}
                </TonePill>
              ))}
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------
function AutomatedDecisions({ capabilities }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData(await plGet("/automated-decisions")); }
    catch (err) { toast.error(legalError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;

  const review = async (id) => {
    const outcome = window.prompt("Outcome (upheld / overturned):");
    if (!outcome) return;
    const note = window.prompt("What was checked, and why is this the outcome?");
    if (!note) return;
    try {
      await plPost(`/automated-decisions/${id}/review`, { outcome, note });
      toast.success("Review recorded.");
      load();
    } catch (err) {
      toast.error(legalError(err));
    }
  };

  return (
    <>
      <Section testId="pl-automated-framework" title="What automated systems may do"
               description={data.framework.note}>
        <Table head={["System", "May", "May not", "Human review"]} testId="pl-framework-table"
               minWidth={700}>
          {data.framework.systems.map((row) => (
            <tr key={row.system} data-testid={`pl-system-${row.system}`}>
              <td className="py-2 pr-3 font-medium text-slate-900">{row.label}</td>
              <td className="py-2 pr-3 text-xs text-slate-700">{row.may}</td>
              <td className="py-2 pr-3 text-xs text-slate-700">{row.may_not}</td>
              <td className="py-2 pr-3 text-xs text-slate-600">{row.human_review}</td>
            </tr>
          ))}
        </Table>
        <div className="mt-3">
          <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
            Reserved to people
          </div>
          <ul className="mt-1 list-disc pl-5 text-xs text-slate-700">
            {data.framework.reserved_to_humans.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </div>
      </Section>

      <Section testId="pl-automated-decisions" title="Recorded automated actions">
        {data.decisions.length === 0 ? (
          <Empty testId="pl-automated-empty">Nothing recorded yet.</Empty>
        ) : (
          <Table head={["When", "System", "Action", "Reason shown", "Review"]}
                 testId="pl-automated-table" minWidth={700}>
            {data.decisions.slice(0, 50).map((row) => (
              <tr key={row.id} data-testid={`pl-decision-${row.id}`}>
                <td className="py-2 pr-3 text-xs">{fmtDateTime(row.at)}</td>
                <td className="py-2 pr-3 text-xs">{row.system_label}</td>
                <td className="py-2 pr-3 text-xs">
                  {humanise(row.action_type)}
                  <div className="text-slate-500">{row.reason_code}</div>
                </td>
                <td className="py-2 pr-3 text-xs">{row.user_visible_reason}</td>
                <td className="py-2 pr-3 text-xs">
                  {humanise(row.review_state)}
                  {can(capabilities, CAP.safety)
                    && ["requested", "in_review"].includes(row.review_state) && (
                    <Button variant="secondary" className="mt-1" onClick={() => review(row.id)}>
                      Review
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </>
  );
}
